/**
 * useIntegrationSectionAccess — React bridge for `getIntegrationSectionAccess`.
 *
 * Reads the admin flag and the project's billing access snapshot, then resolves which
 * Integrations sections the viewer sees. Admins bypass billing (UI admin bypass).
 */

import { useMemo } from 'react';
import { useAuth } from '@frontend/contexts/AuthContext';
import { useBillingAccess } from '@modules/billing-access';
import { getIntegrationSectionAccess, type IntegrationSectionAccess } from '../access';

export interface UseIntegrationSectionAccessResult extends IntegrationSectionAccess {
  /** True until the admin flag and (for non-admins) the access snapshot are known. */
  loading: boolean;
}

export function useIntegrationSectionAccess(projectId: string | undefined): UseIntegrationSectionAccessResult {
  const { isAdmin, adminLoaded } = useAuth() as { isAdmin?: boolean; adminLoaded?: boolean };
  const admin = Boolean(isAdmin);
  const ready = adminLoaded ?? true;
  const billing = useBillingAccess(projectId, { disabled: !ready || admin });

  return useMemo(() => {
    const waitingForAccess = !admin && Boolean(projectId) && !billing.access && !billing.error;
    const loading = !ready || (!admin && (billing.loading || waitingForAccess));
    // While loading nothing is open, so the page never flashes sections the viewer cannot use.
    const access = getIntegrationSectionAccess(loading ? undefined : billing.can, admin);
    return { ...access, loading };
  }, [admin, billing, projectId, ready]);
}
